import * as React from "react";

import { NonIdealState } from "@blueprintjs/core";
import { Set } from "immutable";
import Loader from "~/components/Loader";
import ProductOptionApprovalForm from "~/components/ProductOptionApproval/ProductOptionApprovalForm";
import client from "~/graphql/client";
import {
  APPROVE_MODERATION_OPTIONS_MUTATION,
  ApproveModerationOptions,
  ApproveModerationOptionsVariables
} from "~/graphql/mutations/optionApproval/approveModerationOptions";
import {
  IModerationOption,
  OPTIONS_PENDING_APPROVAL_QUERY,
  OptionsPendingApproval,
  OptionsPendingApprovalVariables
} from "~/graphql/queries/optionApproval/optionsPendingApproval";

interface IState {
  moderationOptions: IModerationOption[];
  approvedIds: Set<string>;
  rejectedIds: Set<string>;
  loading: boolean;
}

export default class ProductOptionApprovalContainer extends React.Component<
  {},
  IState
> {
  public readonly state: IState = {
    approvedIds: Set([]),
    loading: false,
    moderationOptions: [],
    rejectedIds: Set([])
  };

  public render() {
    const { moderationOptions, rejectedIds, approvedIds, loading } = this.state;

    if (loading) {
      return <Loader />;
    }
    if (!moderationOptions.length) {
      return (
        <NonIdealState
          title="All done for now"
          description="Please check back later or work on other lines"
        />
      );
    }

    return (
      <ProductOptionApprovalForm
        moderationOptions={moderationOptions}
        rejectedIds={rejectedIds}
        approvedIds={approvedIds}
        onBulkToggle={this.handleBulkToggle}
        onApprove={this.handleApprove}
        onReject={this.handleReject}
        onSubmit={this.handleSubmit}
      />
    );
  }

  public componentDidMount() {
    this.loadOptions();
  }

  private async loadOptions(count = 200) {
    this.setState({ loading: true });

    const { data } = await client.query<
      OptionsPendingApproval,
      OptionsPendingApprovalVariables
    >({
      fetchPolicy: "network-only",
      query: OPTIONS_PENDING_APPROVAL_QUERY,
      variables: { count }
    });

    if (!data.moderationOptions || !data.moderationOptions.length) {
      return this.setState({
        approvedIds: Set([]),
        loading: false,
        moderationOptions: [],
        rejectedIds: Set([])
      });
    }

    this.setState({
      approvedIds: Set(data.moderationOptions.map(mo => mo.id)),
      loading: false,
      moderationOptions: data.moderationOptions,
      rejectedIds: Set([])
    });
  }

  private submit(approvedIds: string[], rejectedIds: string[]) {
    client.mutate<ApproveModerationOptions, ApproveModerationOptionsVariables>({
      mutation: APPROVE_MODERATION_OPTIONS_MUTATION,
      variables: {
        input: {
          approveModerationOptionIds: approvedIds,
          rejectModerationOptionIds: rejectedIds
        }
      }
    });
  }

  private handleBulkToggle = () => {
    const { approvedIds, moderationOptions } = this.state;
    if (approvedIds.count() === 0) {
      this.setState({
        approvedIds: Set(moderationOptions.map(mo => mo.id)),
        rejectedIds: Set([])
      });
    } else {
      this.setState({
        approvedIds: Set([]),
        rejectedIds: Set(moderationOptions.map(mo => mo.id))
      });
    }
  };

  private handleSubmit = () => {
    const { approvedIds, rejectedIds, loading } = this.state;

    if (loading) {
      return;
    }

    this.loadOptions();
    this.submit(approvedIds.toArray(), rejectedIds.toArray());
  };

  private handleApprove = (moderationOptionId: string) => {
    this.setState(state => ({
      approvedIds: state.approvedIds.add(moderationOptionId),
      rejectedIds: state.rejectedIds.delete(moderationOptionId)
    }));
  };

  private handleReject = (moderationOptionId: string) => {
    this.setState(state => ({
      approvedIds: state.approvedIds.delete(moderationOptionId),
      rejectedIds: state.rejectedIds.add(moderationOptionId)
    }));
  };
}



// WEBPACK FOOTER //
// ./src/containers/ProductOptionApprovalContainer.tsx